import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { listCatalogs } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { usePageTitle } from '../../../layout/PageTitleContext';
import TemplateStudioEditor from '../editor/TemplateStudioEditor';
import {
  compileTemplateSpecForEditor,
  defaultTemplateSpec,
  serializeTemplateSpec,
} from '../private/templateSpecUtils';
import {
  folderDisplayName,
  isValidFileEditSession,
  readBrowserCatalogTemplateFiles,
  readCatalogTemplateFiles,
  saveCatalogTemplateSession,
} from '../private/desktopCatalogTemplateFiles';
import useCatalogTemplateDesigner from '../hooks/useCatalogTemplateDesigner';

const buildDocumentFromFiles = (html, css) => {
  const base = defaultTemplateSpec();
  const spec = {
    ...base,
    html: String(html ?? '').trim() ? html : base.html,
    css: css ?? base.css ?? '',
  };
  try {
    return serializeTemplateSpec(compileTemplateSpecForEditor(spec));
  } catch {
    return serializeTemplateSpec(spec);
  }
};

const TemplateFileEditorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const isDesigner = useCatalogTemplateDesigner();
  const sessionRef = useRef(location.state || null);

  const source = sessionRef.current?.source || 'desktop';
  const folderPath = sessionRef.current?.folderPath || '';
  const folderName = sessionRef.current?.folderName || folderDisplayName(folderPath);
  const initialHtml = sessionRef.current?.html;
  const initialCss = sessionRef.current?.css;
  const dirHandle = sessionRef.current?.dirHandle || null;

  const [documentJson, setDocumentJson] = useState('');
  const [demoCatalogId, setDemoCatalogId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '' });
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    setPageTitle(translate('catalog.templates.edit_file'));
    return () => setPageTitle(null);
  }, [setPageTitle, translate]);

  useEffect(() => {
    if (!isDesigner) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await listCatalogs({ page: 0, page_size: 1 });
        const items = res?.data?.catalogs || res?.data?.items || res?.data || [];
        if (!cancelled && Array.isArray(items) && items[0]?.id) {
          setDemoCatalogId(items[0].id);
        }
      } catch {
        /* optional demo catalog */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDesigner]);

  useEffect(() => {
    if (!isValidFileEditSession(sessionRef.current)) {
      navigate('/catalogs/templates', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        let html = initialHtml;
        let css = initialCss;
        if (html === undefined || css === undefined) {
          if (source === 'browser') {
            const files = await readBrowserCatalogTemplateFiles(dirHandle);
            html = files.html;
            css = files.css;
          } else {
            const files = await readCatalogTemplateFiles(folderPath);
            html = files.html;
            css = files.css;
          }
        }
        if (!cancelled) {
          setDocumentJson(buildDocumentFromFiles(html, css));
          setPreviewRefreshKey((value) => value + 1);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dirHandle, folderPath, initialCss, initialHtml, navigate, source, translate]);

  const handleDocumentChange = useCallback((serialized) => {
    setDocumentJson(serialized);
    setPreviewRefreshKey((value) => value + 1);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const rawDocJson = editorRef.current?.getDocumentJson?.() ?? documentJson;
      const parsed = JSON.parse(rawDocJson);
      await saveCatalogTemplateSession(sessionRef.current, {
        html: parsed.html ?? '',
        css: parsed.css ?? '',
      });
      setSnack({ open: true, message: translate('catalog.templates.edit_file_saved') });
    } catch (err) {
      setError(err.message);
      setSnack({ open: true, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isDesigner) {
    return (
      <Alert severity="warning">{translate('catalog.templates.designer_only')}</Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{translate('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/catalogs/templates')}>
          {translate('common.back')}
        </Button>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 480 } }}
          title={folderPath}
        >
          {translate('catalog.templates.edit_file_folder')}: {folderName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          onClick={() => void handleSave()}
          sx={{ ml: { sm: 'auto' } }}
        >
          {translate('common.save')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {demoCatalogId ? (
        <TemplateStudioEditor
          ref={editorRef}
          documentJson={documentJson}
          onDocumentChange={handleDocumentChange}
          catalogId={demoCatalogId}
          refreshKey={previewRefreshKey}
          fileMode
        />
      ) : (
        <Alert severity="info">{translate('catalog.templates.preview_requires_catalog')}</Alert>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack({ open: false, message: '' })}
        message={snack.message}
      />
    </Box>
  );
};

export default TemplateFileEditorPage;
